import { submitFormJson } from 'widgets/toolbox/ajax';
import { get } from 'widgets/toolbox/util';

/**
 * @typedef {ReturnType<typeof import('widgets/checkout/AddressCheckoutForm').default>} AddressCheckoutForm
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/BasicInput').default>>} basicInput
 * @typedef {InstanceType<ReturnType<typeof import('widgets/forms/InputCheckbox').default>>} inputCheckbox
 * @typedef {InstanceType<ReturnType<typeof import('widgets/checkout/RadioSelector').default>>} radioSelector
 * @typedef {InstanceType<ReturnType<typeof import('widgets/checkout/PaymentAccordion').default>>} paymentAccordion
 * @typedef {InstanceType<ReturnType<typeof import('widgets/global/Label').default>>} label
 */

/**
 * @param {AddressCheckoutForm} AddressCheckoutForm Base widget for extending
 * @returns {typeof BillingForm} Billing Form
 */
export default function (AddressCheckoutForm) {
    /**
     * @category widgets
     * @subcategory checkout
     * @class BillingForm
     * @augments AddressCheckoutForm
     * @classdesc Represents checkout billing form with next features:
     * 1. Handles form submission
     * 2. Handles form submission server response
     * 3. Clears form sensitive data
     * 4. Toggles use shipping address functionality
     * 5. Prepares address options for an address list
     *
     * Widget has next relationship:
     * * {@link PaymentAccordion#validate} is used for payment validation before form submission
     * * {@link PaymentAccordion#process} is used before form submission to get payment method
     * * {@link PaymentAccordion#cleanupSensitiveData} is used to clear sensitive payment data
     * * {@link PaymentAccordion#handleError} is used to show payment method errors
     * * {@link Label#toggle} is used to show/hide use shipping block summary
     * * {@link InputCheckbox#getValue} is used to get state of `use shipping address` checkbox
     * @example <caption>Checkout shipping form</caption>
     * <form
     *     novalidate
     *     method="POST"
     *     action="${URLUtils.url('CheckoutServices-SubmitPayment')}"
     *     data-widget="billingForm"
     *     data-address-selector="billingAddressSelector"
     *     data-widget-event-submit="billingSubmitted"
     *     data-address-fields="${[
     *          billingFields.firstName.htmlName,
     *          billingFields.lastName.htmlName,
     *          billingFields.address1.htmlName,
     *          billingFields.address2.htmlName,
     *          billingFields.country.htmlName,
     *          billingFields.states.stateCode.htmlName,
     *          billingFields.city.htmlName,
     *          billingFields.postalCode.htmlName,
     *          contactInfo.phone.htmlName
     *     ].join(',')}"
     *     data-form-definition-url="${URLUtils.url('Forms-FormDefinition', 'form', 'billing')}"
     *     <isprint value=${pdict.forms.billingForm.attributes} encoding="off" />
     *     data-address-autocomplete-enabled="${!!require('util/sitepref').getValue('googleAutocompleteEnabled')}"
     *>
     *     <div
     *          role="alert"
     *          class="b-form-message"
     *          hidden="hidden"
     *          data-ref="errorMessageLabel"
     *     ></div>
     *     ...
     *     <isinclude template="m/checkout/billing/addressSelector" />
     *     ...
     *     <isinclude template="checkout/billing/billingAddressInner" />
     *     ...
     *     <isinclude template="checkout/billing/paymentOptions" />
     * </form>
     */
    class BillingForm extends AddressCheckoutForm {
        prefs() {
            return {
                errorMessageLabelRef: 'paymentErrorMessage',
                ...super.prefs()
            };
        }

        /**
         * @description Validate form and process submission
         * @returns {void}
         */
        handleSubmit() {
            if (this.isChildrenValid() && !this.submitting) {
                this.submitting = true;

                this.showProgressBar();
                this.ref(this.prefs().submitButton).disable();
                this.ref(this.prefs().errorMessageLabel).hide();

                this.getById('payment', (/** @type {paymentAccordion} */payment) => {
                    payment.process().then(data => {
                        return submitFormJson(
                            this.getFormUrl(),
                            { ...this.getFormFields(), ...data },
                            this.ref('self').attr('method') === 'GET' ? 'GET' : 'POST'
                        );
                    })
                        .then(this.onSubmitted.bind(this))
                        .catch((err) => {
                            if (err.message) {
                                this.setError(err.message, true, this.prefs().errorMessageLabelRef);
                            }
                        })
                        .finally(this.afterSubmission.bind(this));
                });
            }
        }

        /**
         * @description Clear payment sensitive data
         * @returns {void}
         */
        cleanupSensitiveData() {
            this.getById('payment', (/** @type {paymentAccordion} */payment) => {
                payment.data('cleanupSensitiveData');
            });
        }

        /**
         * @description Process form submission response
         * @param {object} data - Server response object
         * @returns {void}
         */
        onSubmitted(data) {
            if (data.error && data.serverErrors && data.serverErrors.length) {
                this.setError(data.serverErrors.join(''));
            } else if (data.error && data.fieldErrors && data.fieldErrors.length) {
                data.fieldErrors.forEach((entries) => {
                    Object.entries(entries).forEach(([name, errorMsg]) => {
                        this.getById(name, (/** @type {basicInput} */address) => {
                            if (address.id === name) {
                                address.setError(errorMsg);
                            } else {
                                this.getById('payment', (/** @type {paymentAccordion} */payment) => {
                                    payment.handleError(name, errorMsg);
                                });
                            }
                        });
                    });
                });
            } else {
                const billingAddressID = get(data, 'order.billing.billingAddress.address.addressUUID');
                this.setSelectedAddress(billingAddressID);
                super.onSubmitted(data);
                this.cleanupSensitiveData();
            }
        }

        /**
         * @description Activate billing step
         * @param {object} param - Activation params object
         * @param {object} param.order - Order object
         * @param {object} param.customer - Customer object
         * @returns {void}
         */
        activate({ order, customer }) {
            this.cleanupSensitiveData();
            this.getById('useShipping', (/** @type {inputCheckbox} */useShippingCheckbox) => {
                if (useShippingCheckbox.getValue()) {
                    this.setSelectedAddress(this.getShippingAddressID(order));
                }
            });
            super.activate({ order, customer });
        }

        /**
         * @description Toggle use shipping functionality after address selector activation
         * @returns {void}
         */
        onAddressSelectorActivated() {
            this.getById('useShipping', this.toggleUseShipping);
        }

        /**
         * @description Toggle use shipping functionality after address selector activation
         * @param {inputCheckbox} useShippingCheckbox - Use shipping checkbox component
         * @returns {void}
         */
        toggleUseShipping(/** @type {inputCheckbox} */useShippingCheckbox) {
            const checked = Boolean(useShippingCheckbox.getValue());

            if (checked) {
                this.setSelectedAddress(this.getShippingAddressID());
                this.hideAddressFields();
                this.hideSavedAddresses();
                this.callIfExists('focusFirstInput'); // class AccessibilityFocusMixin, method focusFirstInput
            } else if (!get(this, 'customer.addresses.length')) {
                this.showAddressForm();
                this.callIfExists('focusFirstInput', 'addressForm'); // class AccessibilityFocusMixin, method focusFirstInput
            }

            if (checked || get(this, 'customer.addresses.length')) {
                this.getById(this.prefs().addressSelector, (/** @type {radioSelector} */addressSelector) => {
                    if (this.selectedAddressID && addressSelector.data('value') !== this.selectedAddressID) {
                        this.updateAddressSelector(() => {
                            this.fulfillAddress();
                            addressSelector.toggle(!checked);
                        });
                    } else {
                        this.fulfillAddress();
                        addressSelector.toggle(!checked);
                    }
                });
            }

            this.getById('useShippingDescription', (/** @type {label} */ useShippingDescription) => {
                useShippingDescription.toggle(checked);
            });
        }

        /**
         * @description Initialize saved addresses selector
         * @returns {Promise| undefined} result
         */
        activateAddressSelector() {
            this.getById('useShippingDescription', (/** @type {label} */ useShippingDescription) => {
                useShippingDescription.updateTemplate({
                    order: this.order
                }).then(() => {
                    this.getById('useShipping', (/** @type {inputCheckbox} */useShippingCheckbox) => {
                        useShippingDescription.toggle(Boolean(useShippingCheckbox.getValue()));
                    });
                });
            });
            return super.activateAddressSelector();
        }

        /**
         * @description Return shipping address ID from order object
         * @param {object} order - order data
         * @returns {string} returns shipping addresses ID
         */
        getShippingAddressID(order = this.order) {
            return get(order, 'shipping.0.shippingAddress.addressUUID', '');
        }

        /**
         * @description Return applicable addresses for current form
         * @param {object} order - order data
         * @param {object} customer - customer data
         * @returns {Array} returns billing addresses
         */
        getAddressOptions(order, customer) {
            const billingAddress = get(order, 'billing.billingAddress.address');
            const shippingAddress = get(order, 'shipping.0.shippingAddress');
            const customerAddresses = customer.addresses;
            const allAddresses = [...customerAddresses];

            if (shippingAddress) {
                allAddresses.unshift(shippingAddress);
            }
            if (billingAddress) {
                allAddresses.unshift(billingAddress);
            }

            return allAddresses;
        }

        hidePaymentError() {
            this.clearError(this.prefs().errorMessageLabelRef);
        }
    }

    return BillingForm;
}
